import type { AppDispatch, RootState } from '../../store';
import { clearDxfEquipmentOverlays, setDxfEquipmentOverlays } from './equipmentSlice';
import {
  extractEquipmentFromDxfJson,
  processExtractedEquipment,
} from '../../utils/dxfEquipmentExtractor';
import { getDxfEquipmentSource } from '../../utils/dxfEquipmentSource';
import {
  isDxfDerivedEquipment,
  isEquipmentVisibleForDxfLayers,
} from '../../utils/equipmentGeometry';
import { collectExistingEntityIds, createEntityIdAllocator } from '../../utils/entityIds';
import type { EquipmentInstance } from './equipmentTypes';

/**
 * Re-issue ids for a cached extraction. It was produced during import, before
 * the editor held anything, so its ids were allocated against an empty pool and
 * can collide with layout elements or hand-drawn equipment.
 */
function withFreshIds(
  instances: EquipmentInstance[],
  reservedIds: Iterable<string>
): EquipmentInstance[] {
  const alloc = createEntityIdAllocator(reservedIds);
  return instances.map((inst) => ({ ...inst, id: alloc.nextForEquipment(inst.objectType) }));
}

export type GenerateDxfOverlaysResult = {
  ok: boolean;
  /** Overlays kept after filtering out hidden DXF layers. */
  count: number;
  /** Overlays dropped because their DXF layer (or INSERT type) is hidden. */
  hiddenCount: number;
  message: string;
};

/**
 * Rebuild the DXF equipment overlays from the imported lossless JSON, keeping
 * only the ones whose DXF layer is currently visible.
 *
 * Generation is destructive on purpose: overlays for hidden layers are never
 * added to the store, so they cannot end up in an exported layout, a backend
 * save, or a simulation payload. Callers re-run this whenever layer visibility
 * changes so the overlay set always matches what the user can see.
 */
export const generateDxfEquipmentOverlays =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): GenerateDxfOverlaysResult => {
    const source = getDxfEquipmentSource();
    if (!source) {
      return {
        ok: false,
        count: 0,
        hiddenCount: 0,
        message: 'Import a floor plan before generating equipment overlays.',
      };
    }

    const state = getState();
    const { layers, hiddenEntityTypes } = state.floorplan;

    // Hand-drawn equipment survives regeneration, and layout elements share the
    // same id space, so both must be reserved before new ids are issued.
    const reservedIds = collectExistingEntityIds(
      Object.values(state.layout.elements),
      Object.values(state.equipment.instances).filter((inst) => !isDxfDerivedEquipment(inst))
    );

    let extracted: EquipmentInstance[];
    try {
      extracted = extractEquipmentFromDxfJson(
        source.document,
        source.transform,
        undefined,
        undefined,
        reservedIds
      );
    } catch (err) {
      console.error('Failed to extract equipment overlays from DXF JSON:', err);
      return {
        ok: false,
        count: 0,
        hiddenCount: 0,
        message: 'Could not read equipment from the imported floor plan.',
      };
    }

    // The worker-backed load path discards the entity tree after parsing
    // geometry, so its cached document is an empty stub and re-extraction finds
    // nothing. Fall back to the extraction that import already computed.
    if (extracted.length === 0 && source.extractedEquipment?.length) {
      extracted = withFreshIds(source.extractedEquipment, reservedIds);
    }

    const visible = extracted.filter((inst) =>
      isEquipmentVisibleForDxfLayers(inst, layers, hiddenEntityTypes)
    );
    const hiddenCount = extracted.length - visible.length;

    if (visible.length === 0) {
      dispatch(clearDxfEquipmentOverlays({ keepGenerated: extracted.length > 0 }));
      return {
        ok: true,
        count: 0,
        hiddenCount,
        message:
          extracted.length === 0
            ? 'No equipment footprints found in this floor plan.'
            : 'All equipment overlays belong to hidden DXF layers.',
      };
    }

    dispatch(setDxfEquipmentOverlays(processExtractedEquipment(visible)));

    return {
      ok: true,
      count: visible.length,
      hiddenCount,
      message:
        hiddenCount > 0
          ? `Detected ${visible.length} potential equipment; skipped ${hiddenCount} on hidden layers.`
          : `Detected ${visible.length} potential equipment.`,
    };
  };

/** Destroy all DXF-generated overlays (hand-drawn equipment is kept). */
export const clearGeneratedDxfEquipmentOverlays =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(clearDxfEquipmentOverlays({ clearReviewDecisions: true }));
  };
